import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Sparkles, Mail, CheckCircle, Key } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // 'email', 'token', 'success'
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      toast.success('If an account exists with this email, you will receive a reset link');
      
      // For demo purposes, if token is returned, show it
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
      }
      setStep('token');
    } catch (error) {
      toast.error('Failed to send reset request');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);

    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      toast.success('Password reset successfully!');
      setStep('success');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to reset password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="forgot-password-page">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-[#722F37] to-[#4A7C59]">
        <div className="absolute inset-0 bg-black/10" />
        
        <div className="relative z-10 p-16 flex flex-col justify-between w-full">
          <Link to="/" className="flex items-center gap-2">
            <span className="text-2xl font-bold text-white">
              Funtz<span className="text-white/80">Guntz</span>AI
            </span>
          </Link>
          
          <div>
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-8">
              <Key className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-5xl font-bold text-white mb-6 leading-tight">
              Reset your password
            </h1>
            <p className="text-xl text-white/80 max-w-md">
              We'll help you get back to creating amazing fonts in no time
            </p>
          </div>
          
          <p className="text-sm text-white/60">
            © 2024 FuntzGuntzAI
          </p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile back link */}
          <Link 
            to="/login" 
            className="lg:hidden flex items-center gap-2 text-[#475569] hover:text-[#0F172A] mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </Link>

          {step === 'email' && (
            <>
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-[#0F172A] mb-3">
                  Forgot password?
                </h2>
                <p className="text-[#475569]">
                  Enter your email and we'll send you a reset link
                </p>
              </div>

              <form onSubmit={handleRequestReset} className="space-y-6" data-testid="forgot-password-form">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-[#0F172A]">
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="input-terminal w-full"
                    placeholder="you@example.com"
                    required
                    data-testid="email-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#722F37] hover:bg-[#5A252C] text-white py-6 text-base font-medium rounded-xl shadow-sm"
                  data-testid="send-reset-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4 mr-2" />
                      Send reset link
                    </>
                  )}
                </Button>

                <p className="text-center text-[#475569] mt-6">
                  Remember your password?{' '}
                  <Link 
                    to="/login" 
                    className="text-[#722F37] hover:text-[#5A252C] font-medium"
                  >
                    Sign in
                  </Link>
                </p>
              </form>
            </>
          )}

          {step === 'token' && (
            <>
              <div className="mb-10">
                <h2 className="text-3xl font-bold text-[#0F172A] mb-3">
                  Reset your password
                </h2>
                <p className="text-[#475569]">
                  Enter the reset token and your new password
                </p>
              </div>

              <form onSubmit={handleResetPassword} className="space-y-6" data-testid="reset-password-form">
                <div className="space-y-2">
                  <Label htmlFor="token" className="text-sm font-medium text-[#0F172A]">
                    Reset Token
                  </Label>
                  <Input
                    id="token"
                    type="text"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    className="input-terminal w-full"
                    placeholder="Paste your reset token"
                    required
                    data-testid="token-input"
                  />
                  <p className="text-xs text-[#64748B]">
                    Check your email for the reset token
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-sm font-medium text-[#0F172A]">
                    New Password
                  </Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="input-terminal w-full"
                    placeholder="••••••••"
                    required
                    data-testid="new-password-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-sm font-medium text-[#0F172A]">
                    Confirm New Password
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="input-terminal w-full"
                    placeholder="••••••••"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#722F37] hover:bg-[#5A252C] text-white py-6 text-base font-medium rounded-xl shadow-sm"
                  data-testid="reset-password-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    'Reset password'
                  )}
                </Button>

                <button
                  type="button"
                  onClick={() => setStep('email')}
                  className="w-full text-center text-[#475569] hover:text-[#0F172A] mt-4"
                >
                  ← Back to email
                </button>
              </form>
            </>
          )}

          {step === 'success' && (
            <div className="text-center">
              <div className="w-20 h-20 mx-auto flex items-center justify-center bg-green-500/10 rounded-full mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h2 className="text-3xl font-bold text-[#0F172A] mb-3">
                Password reset!
              </h2>
              <p className="text-[#475569] mb-8">
                Your password has been reset successfully. You can now sign in with your new password.
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-[#722F37] hover:bg-[#5A252C] text-white py-6 text-base font-medium rounded-xl shadow-sm"
                data-testid="go-to-login-btn"
              >
                Go to Sign in
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
